"use client";

import * as React from "react";
import { Suspense } from "react";
import { signIn } from "next-auth/react";
import { useSearchParams } from "next/navigation";

function SignInInner() {
  const sp = useSearchParams();
  const callbackUrl = sp.get("callbackUrl") ?? "/";
  const error = sp.get("error");

  return (
    <main style={shellStyle}>
      <BackgroundGradient />
      <section style={cardStyle} className="signin-card">
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 22 }}>
          <Logo />
          <div style={{ textAlign: "center" }}>
            <h1 style={titleStyle}>Welcome to xcalitex</h1>
            <p style={subtitleStyle}>
              Excalidraw + LaTeX, persisted on the server. Sign in with Google to access
              your private boards.
            </p>
          </div>
          <button
            type="button"
            onClick={() => signIn("google", { callbackUrl })}
            style={googleBtnStyle}
            className="g-btn"
          >
            <GoogleG />
            <span>Continue with Google</span>
          </button>
          {error ? (
            <div style={errorStyle}>
              {error === "AccessDenied"
                ? "Sign-in was rejected. If you just registered the OAuth client, double-check the redirect URI."
                : `Sign-in failed (${error}). Try again or check the server logs.`}
            </div>
          ) : null}
          <p style={legalStyle}>
            By signing in, you agree to give yourself access to a video conferencing tool you
            already host.
          </p>
        </div>
      </section>
    </main>
  );
}

function Logo() {
  // xcalitex = Excalidraw + LaTeX. Tilted sketchpad square with an italic
  // a/b fraction centered, plus a tiny pencil. Distinct from jobu's
  // infinity-loops camera-aperture icon.
  return (
    <div className="logo-pop" style={{
      width: 76, height: 76,
      display: "grid", placeItems: "center",
      borderRadius: 22,
      background: "linear-gradient(135deg, #1f2540 0%, #0c0f1f 100%)",
      boxShadow: "0 12px 28px -8px rgba(91,141,239,0.4)",
      position: "relative",
      overflow: "hidden",
    }}>
      <svg width="56" height="56" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="xc-pad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#5b8def" />
            <stop offset="100%" stopColor="#7c5bef" />
          </linearGradient>
        </defs>
        <g transform="rotate(-6 32 32)">
          <rect x="10" y="10" width="44" height="44" rx="8" fill="url(#xc-pad)" stroke="rgba(255,255,255,0.18)" strokeWidth="1" />
          <text x="32" y="30" textAnchor="middle"
            fontFamily="'Cambria Math', 'KaTeX_Math', 'Times New Roman', serif"
            fontStyle="italic" fontSize="18" fontWeight={600}
            fill="#fff">a</text>
          <line x1="24" y1="34" x2="40" y2="34" stroke="#fff" strokeWidth="1.6" strokeLinecap="round" />
          <text x="32" y="48" textAnchor="middle"
            fontFamily="'Cambria Math', 'KaTeX_Math', 'Times New Roman', serif"
            fontStyle="italic" fontSize="18" fontWeight={600}
            fill="#fff">b</text>
        </g>
        <g transform="translate(46 8) rotate(35)">
          <rect x="-1.5" y="-2" width="14" height="4" rx="1.2" fill="#f7c948" />
          <polygon points="12.5,-2 16,0 12.5,2" fill="#0c0f1f" />
        </g>
      </svg>
    </div>
  );
}

function GoogleG() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="#4285F4"
        d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.44c-.28 1.46-1.12 2.69-2.39 3.52v2.93h3.86c2.26-2.08 3.58-5.15 3.58-8.69z"
      />
      <path
        fill="#34A853"
        d="M12 24c3.24 0 5.95-1.07 7.93-2.91l-3.86-2.93c-1.07.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.11C3.26 21.3 7.31 24 12 24z"
      />
      <path
        fill="#FBBC05"
        d="M5.27 14.36c-.25-.72-.38-1.49-.38-2.36s.14-1.64.38-2.36V6.53H1.29C.47 8.16 0 9.99 0 12s.47 3.84 1.29 5.47l3.98-3.11z"
      />
      <path
        fill="#EA4335"
        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.69 1.29 6.53l3.98 3.11C6.22 6.86 8.87 4.75 12 4.75z"
      />
    </svg>
  );
}

function BackgroundGradient() {
  return (
    <>
      <div className="bg-blob blob-a" />
      <div className="bg-blob blob-b" />
      <style>{`
        .bg-blob {
          position: absolute;
          width: 60vw; height: 60vw; max-width: 700px; max-height: 700px;
          border-radius: 50%;
          filter: blur(80px);
          opacity: 0.45;
          pointer-events: none;
          z-index: 0;
        }
        .blob-a { top: -15%; left: -10%; background: radial-gradient(closest-side, #5b8def, transparent 70%); animation: drift-a 18s ease-in-out infinite; }
        .blob-b { bottom: -15%; right: -10%; background: radial-gradient(closest-side, #9b6cf4, transparent 70%); animation: drift-b 22s ease-in-out infinite; }
        @keyframes drift-a { 0%,100% { transform: translate(0,0); } 50% { transform: translate(40px,30px); } }
        @keyframes drift-b { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-30px,-40px); } }
        .signin-card { animation: card-in 600ms cubic-bezier(.2,.8,.2,1); }
        @keyframes card-in {
          0% { opacity: 0; transform: translateY(16px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .logo-pop { animation: logo-pop 700ms cubic-bezier(.2,1.6,.6,1); }
        @keyframes logo-pop {
          0% { opacity: 0; transform: scale(0.4) rotate(-12deg); }
          100% { opacity: 1; transform: scale(1) rotate(0); }
        }
        .g-btn:hover {
          transform: translateY(-1px);
          box-shadow: 0 8px 22px -6px rgba(0,0,0,0.45);
        }
        .g-btn:active { transform: translateY(0); }
      `}</style>
    </>
  );
}

const shellStyle: React.CSSProperties = {
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "24px 16px",
  background: "#06070f",
  color: "#e8ecff",
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  position: "relative",
  overflow: "hidden",
};

const cardStyle: React.CSSProperties = {
  position: "relative",
  zIndex: 1,
  width: "100%",
  maxWidth: 420,
  padding: "44px 32px",
  borderRadius: 22,
  background: "rgba(20, 24, 44, 0.55)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
  boxShadow: "0 32px 80px -24px rgba(0,0,0,0.6)",
  backdropFilter: "blur(24px) saturate(140%)",
  WebkitBackdropFilter: "blur(24px) saturate(140%)",
};

const titleStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 24,
  letterSpacing: -0.4,
  fontWeight: 700,
  background: "linear-gradient(135deg, #ffffff 0%, #b8c4ff 100%)",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
};

const subtitleStyle: React.CSSProperties = {
  margin: "10px 0 0",
  fontSize: 13,
  opacity: 0.72,
  lineHeight: 1.55,
  textAlign: "center",
};

const googleBtnStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: 10,
  padding: "12px 20px",
  fontSize: 14,
  fontWeight: 600,
  background: "#ffffff",
  color: "#1f1f1f",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: 10,
  cursor: "pointer",
  transition: "transform 150ms, box-shadow 200ms",
  boxShadow: "0 4px 14px -4px rgba(0,0,0,0.35)",
};

const errorStyle: React.CSSProperties = {
  fontSize: 12,
  padding: "8px 12px",
  borderRadius: 8,
  background: "rgba(255, 102, 102, 0.1)",
  border: "1px solid rgba(255, 102, 102, 0.25)",
  color: "#ff9595",
  maxWidth: 320,
};

const legalStyle: React.CSSProperties = {
  margin: 0,
  fontSize: 11,
  opacity: 0.45,
  textAlign: "center",
  maxWidth: 320,
};

export default function SignInPage() {
  return (
    <Suspense fallback={null}>
      <SignInInner />
    </Suspense>
  );
}
